import type { LegalCitation, ResearchAnswer } from '@buakei/types';

/** DEMO FIXTURES — scripted assistant answers (ADR-004 C). No AI model produced these. */

const cite = (
  id: string,
  sourceId: string,
  versionId: string,
  passageIds: string[],
  label: string,
): LegalCitation => ({ id, sourceId, versionId, passageIds, label, dataOrigin: 'demo' });

const SEARCHED = [
  'Demonstration statutes (1 fictitious Act)',
  'Demonstration judgments (1 fictitious judgment)',
  'Demonstration rules (1 fictitious set of rules)',
];

const base = {
  dataOrigin: 'demo' as const,
  mode: 'quick' as const,
  reviewStatus: 'ai_draft' as const,
  sourceSetSnapshotId: 'snap_demo_2026_10',
  createdAt: '2026-10-19T09:00:00Z',
};

export type ScriptedPrompt = { id: string; label: string; answer: ResearchAnswer };

export const PROFESSIONAL_PROMPTS: ScriptedPrompt[] = [
  {
    id: 'formation',
    label: 'When is a demonstration agreement formed?',
    answer: {
      ...base,
      id: 'ans_demo_formation',
      query: 'When is a demonstration agreement formed?',
      outcome: 'answered',
      summary:
        'Within the demonstration sources, an agreement is formed when an offer is accepted in the manner the offer specifies; silence is not acceptance.',
      propositions: [
        {
          id: 'p1',
          text: 'A demonstration agreement is formed when the offer is accepted in the manner the offer specifies.',
          citationIds: ['c1'],
        },
        {
          id: 'p2',
          text: 'Where an offer requires written acceptance, an oral reply did not form an agreement in the fictitious judgment.',
          citationIds: ['c2'],
          caveat:
            'The judgment text has not passed text-accuracy review, and its legal status could not be established.',
        },
      ],
      citations: [
        cite(
          'c1',
          'src_demo_contracts_act',
          'ver_demo_ca_v1',
          ['psg_demo_ca_s2'],
          'Demonstration Contracts Act (Fictitious), s. 2',
        ),
        cite(
          'c2',
          'src_demo_alpha_v_beta',
          'ver_demo_ab_v1',
          ['psg_demo_ab_p12'],
          'Alpha Example v Beta Illustration (DEMO), para. 12',
        ),
      ],
      coverage: {
        searchedCollections: SEARCHED,
        knownGaps: ['No demonstration appellate decisions are held.'],
        cannotEstablish: [],
      },
    },
  },
  {
    id: 'consideration',
    label: 'Does the demo case decide consideration?',
    answer: {
      ...base,
      id: 'ans_demo_consideration',
      query: 'Does the demo case decide consideration?',
      outcome: 'answered',
      summary:
        'The fictitious judgment expressly did not decide consideration. The demonstration Act states the general rule.',
      propositions: [
        {
          id: 'p1',
          text: 'The fictitious court did not decide whether consideration had been given, because no agreement was formed.',
          citationIds: ['c1'],
        },
        {
          id: 'p2',
          text: 'Under the demonstration Act, a promise is enforceable only where each party gives something of value in exchange.',
          citationIds: ['c2'],
        },
        {
          id: 'p3',
          text: 'A further passage was suggested but could not be resolved to the source, so it is excluded from the evidence.',
          citationIds: ['c3'],
        },
      ],
      citations: [
        cite(
          'c1',
          'src_demo_alpha_v_beta',
          'ver_demo_ab_v1',
          ['psg_demo_ab_p15'],
          'Alpha Example v Beta Illustration (DEMO), para. 15',
        ),
        cite(
          'c2',
          'src_demo_contracts_act',
          'ver_demo_ca_v1',
          ['psg_demo_ca_s3'],
          'Demonstration Contracts Act (Fictitious), s. 3',
        ),
        // Deliberately unresolvable: demonstrates that unknown passages are never shown as evidence.
        cite(
          'c3',
          'src_demo_alpha_v_beta',
          'ver_demo_ab_v1',
          ['psg_demo_ab_p99'],
          'Alpha Example v Beta Illustration (DEMO), para. 99',
        ),
      ],
      coverage: {
        searchedCollections: SEARCHED,
        knownGaps: ['No demonstration commentary or secondary sources are held.'],
        cannotEstablish: ['Whether any later demonstration decision has considered paragraph 15.'],
      },
    },
  },
  {
    id: 'limitation',
    label: 'Is there a limitation period for demo tenancy claims?',
    answer: {
      ...base,
      id: 'ans_demo_limitation',
      query: 'Is there a limitation period for demo tenancy claims?',
      outcome: 'not_found',
      summary:
        'Not found within the searched demonstration sources. This does not mean no such rule exists — only that none was found in the collections listed below.',
      propositions: [],
      citations: [],
      coverage: {
        searchedCollections: SEARCHED,
        knownGaps: [
          'No demonstration limitation statute is held.',
          'The demonstration tenancy rules have not passed authenticity review.',
        ],
        cannotEstablish: ['Any limitation period for demonstration tenancy claims.'],
      },
    },
  },
];

export const LEARNING_PROMPTS: ScriptedPrompt[] = [
  {
    id: 'explain-formation',
    label: 'Explain agreement formation in plain English',
    answer: {
      ...base,
      id: 'ans_demo_learn_formation',
      query: 'Explain agreement formation in plain English',
      outcome: 'answered',
      summary:
        'In the demonstration law, a deal only exists once someone accepts an offer in the way the offer asked for. Staying silent does not count as saying yes.',
      propositions: [
        {
          id: 'p1',
          text: 'Think of an offer as a set of instructions: acceptance must follow those instructions to create an agreement.',
          citationIds: ['c1'],
        },
        {
          id: 'p2',
          text: 'Example from the fictitious case: the offer asked for a written reply, so a phone call was not enough.',
          citationIds: ['c2'],
        },
      ],
      citations: [
        cite(
          'c1',
          'src_demo_contracts_act',
          'ver_demo_ca_v1',
          ['psg_demo_ca_s2'],
          'Demonstration Contracts Act (Fictitious), s. 2',
        ),
        cite(
          'c2',
          'src_demo_alpha_v_beta',
          'ver_demo_ab_v1',
          ['psg_demo_ab_p12'],
          'Alpha Example v Beta Illustration (DEMO), para. 12',
        ),
      ],
      coverage: { searchedCollections: SEARCHED, knownGaps: [], cannotEstablish: [] },
    },
  },
];
